import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts';

// Theme colors hook for dark mode support
const useThemeColors = () => {
  const [isDark, setIsDark] = useState(document.body.classList.contains('dark-mode'));

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.body.classList.contains('dark-mode'));
    };
    
    // Use MutationObserver to detect class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkTheme();
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    // Also check periodically as backup
    const interval = setInterval(checkTheme, 100);
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  return {
    isDark,
    cardBg: isDark ? '#1e293b' : '#ffffff',
    textPrimary: isDark ? '#f1f5f9' : '#333333',
    textSecondary: isDark ? '#94a3b8' : '#666666',
    textMuted: isDark ? '#64748b' : '#888888',
    borderColor: isDark ? '#334155' : '#e2e8f0',
    chartGrid: isDark ? '#334155' : '#e0e0e0',
    chartText: isDark ? '#94a3b8' : '#666666',
    tooltipBg: isDark ? '#1e293b' : '#ffffff',
    tableBg: isDark ? '#1e293b' : '#ffffff',
    tableHeaderBg: isDark ? '#334155' : '#f8f9fa',
    tableHoverBg: isDark ? '#334155' : '#f5f5f5',
    inputBg: isDark ? '#334155' : '#ffffff',
    inputBorder: isDark ? '#475569' : '#ddd',
    inputText: isDark ? '#f1f5f9' : '#333333',
    // Expense specific colors
    profitCardBg: isDark ? '#1e3a2f' : '#E8F5E9',
    profitCardText: isDark ? '#4ade80' : '#388E3C',
    profitCardValue: isDark ? '#22c55e' : '#2E7D32',
    expenseCardBg: isDark ? '#3a1e1e' : '#FFEBEE',
    expenseCardText: isDark ? '#f87171' : '#C62828',
    expenseCardValue: isDark ? '#ef4444' : '#D32F2F',
    netProfitBg: isDark ? '#1e293b' : '#E3F2FD',
    netLossBg: isDark ? '#3a2a1e' : '#FFF3E0',
    netProfitText: isDark ? '#60a5fa' : '#1565C0',
    netLossText: isDark ? '#fb923c' : '#E65100',
    netProfitValue: isDark ? '#3b82f6' : '#1976D2',
    netLossValue: isDark ? '#f97316' : '#F57C00',
  };
};

const Expenses = () => {
  const themeColors = useThemeColors();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ monthly: [], byCategory: [], currentMonthTotal: 0, yearTotal: 0 });
  const [profitLoss, setProfitLoss] = useState({ monthly: [], totalRevenue: 0, totalExpenses: 0, netProfit: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [activeTab, setActiveTab] = useState('expenses');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    categoryId: '',
    categoryName: '',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    description: '',
    vendorName: '',
    receiptNo: ''
  });

  const [newCategory, setNewCategory] = useState({ name: '', icon: '💰' });

  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'];

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [expensesRes, categoriesRes, statsRes, plRes] = await Promise.all([
        axios.get(`/api/expenses?year=${selectedYear}${selectedMonth ? `&month=${selectedMonth}` : ''}${selectedCategory ? `&categoryId=${selectedCategory}` : ''}`, { headers }),
        axios.get('/api/expenses/categories', { headers }),
        axios.get(`/api/expenses/stats?year=${selectedYear}`, { headers }),
        axios.get(`/api/expenses/profit-loss?year=${selectedYear}`, { headers })
      ]);

      setExpenses(expensesRes.data.expenses || []);
      setCategories(categoriesRes.data.categories || []);
      setStats(statsRes.data);
      setProfitLoss(plRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, selectedCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.categoryId || !formData.amount || !formData.expenseDate) {
      setError('Category, amount, and date are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const category = categories.find(c => c.id === formData.categoryId);
      const payload = { ...formData, categoryName: category?.name || 'Unknown' };

      if (editingExpense) {
        await axios.put(`/api/expenses/${editingExpense.id}`, payload, { headers });
      } else {
        await axios.post('/api/expenses', payload, { headers });
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save expense');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/expenses/categories', newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCategoryModal(false);
      setNewCategory({ name: '', icon: '💰' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      setError('Failed to delete expense');
    }
  };

  const resetForm = () => {
    setFormData({
      categoryId: '',
      categoryName: '',
      amount: '',
      expenseDate: new Date().toISOString().split('T')[0],
      description: '',
      vendorName: '',
      receiptNo: ''
    });
    setEditingExpense(null);
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      categoryId: expense.categoryId,
      categoryName: expense.categoryName,
      amount: expense.amount,
      expenseDate: expense.expenseDate?.split('T')[0] || '',
      description: expense.description || '',
      vendorName: expense.vendorName || '',
      receiptNo: expense.receiptNo || ''
    });
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Calculate totals for filtered expenses
  const filteredTotal = useMemo(() => {
    return expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  }, [expenses]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#888' }}>Loading expenses...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: themeColors.textPrimary }}>💰 Expense Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowCategoryModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            + Add Category
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF6B6B',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            + Add Expense
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '5px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {['expenses', 'reports', 'profit-loss'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === tab ? '#FF6B6B' : themeColors.cardBg,
              color: activeTab === tab ? '#fff' : themeColors.textPrimary,
              border: `1px solid ${activeTab === tab ? '#FF6B6B' : themeColors.borderColor}`,
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? 'bold' : 'normal'
            }}
          >
            {tab === 'expenses' ? '📋 Expenses' : tab === 'reports' ? '📊 Reports' : '📈 Profit/Loss'}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
        <div style={{ padding: '20px', backgroundColor: themeColors.cardBg, borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
          <div style={{ fontSize: '14px', color: themeColors.textMuted }}>This Month</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF6B6B' }}>{formatCurrency(stats.currentMonthTotal)}</div>
        </div>
        <div style={{ padding: '20px', backgroundColor: themeColors.cardBg, borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
          <div style={{ fontSize: '14px', color: themeColors.textMuted }}>Year Total ({selectedYear})</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF6B6B' }}>{formatCurrency(stats.yearTotal)}</div>
        </div>
        <div style={{ padding: '20px', backgroundColor: themeColors.cardBg, borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
          <div style={{ fontSize: '14px', color: themeColors.textMuted }}>Net Profit/Loss ({selectedYear})</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: profitLoss.netProfit >= 0 ? '#4ECDC4' : '#FF6B6B' }}>
            {formatCurrency(profitLoss.netProfit)}
          </div>
        </div>
        <div style={{ padding: '20px', backgroundColor: themeColors.cardBg, borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
          <div style={{ fontSize: '14px', color: themeColors.textMuted }}>Revenue ({selectedYear})</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4ECDC4' }}>{formatCurrency(profitLoss.totalRevenue)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{ padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, minWidth: '120px', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
        >
          {[2024, 2025, 2026].map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{ padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, minWidth: '120px', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
        >
          <option value="">All Months</option>
          {monthNames.map((name, idx) => (
            <option key={idx} value={idx + 1}>{name}</option>
          ))}
        </select>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, minWidth: '150px', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
          ))}
        </select>
      </div>

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflow: 'hidden', border: `1px solid ${themeColors.borderColor}` }}>
          <div style={{ padding: '15px', borderBottom: `1px solid ${themeColors.borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', color: themeColors.textPrimary }}>Expenses ({expenses.length})</span>
            <span style={{ color: '#FF6B6B', fontWeight: 'bold' }}>Total: {formatCurrency(filteredTotal)}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: themeColors.tableHeaderBg }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${themeColors.borderColor}`, color: themeColors.textPrimary }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${themeColors.borderColor}`, color: themeColors.textPrimary }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${themeColors.borderColor}`, color: themeColors.textPrimary }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: `1px solid ${themeColors.borderColor}`, color: themeColors.textPrimary }}>Vendor</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: `1px solid ${themeColors.borderColor}`, color: themeColors.textPrimary }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: `1px solid ${themeColors.borderColor}`, color: themeColors.textPrimary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: themeColors.textMuted }}>
                    No expenses found. Click "Add Expense" to create one.
                  </td>
                </tr>
              ) : (
                expenses.map(expense => (
                  <tr key={expense.id} style={{ borderBottom: `1px solid ${themeColors.borderColor}` }}>
                    <td style={{ padding: '12px', color: themeColors.textPrimary }}>
                      {new Date(expense.expenseDate).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', color: themeColors.textPrimary }}>
                      {categories.find(c => c.id === expense.categoryId)?.icon || '💰'} {expense.categoryName}
                    </td>
                    <td style={{ padding: '12px', color: themeColors.textSecondary }}>{expense.description || '-'}</td>
                    <td style={{ padding: '12px', color: themeColors.textSecondary }}>{expense.vendorName || '-'}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#FF6B6B' }}>
                      {formatCurrency(expense.amount)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => openEdit(expense)}
                        className="action-btn edit"
                        style={{ padding: '5px 10px', marginRight: '5px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="action-btn delete"
                        style={{ padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
          {/* Category Breakdown Pie Chart */}
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>Category Breakdown</h3>
            {stats.byCategory?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.byCategory}
                    dataKey="total"
                    nameKey="categoryName"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, categoryName, percent }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text x={x} y={y} fill={themeColors.chartText} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
                          {`${categoryName}: ${(percent * 100).toFixed(0)}%`}
                        </text>
                      );
                    }}
                  >
                    {stats.byCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)} 
                    contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }}
                    labelStyle={{ color: themeColors.textPrimary }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: themeColors.textMuted }}>No data available</div>
            )}
          </div>

          {/* Monthly Expenses Bar Chart */}
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>Monthly Expenses ({selectedYear})</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthNames.map((name, idx) => {
                const monthData = stats.monthly?.find(m => m.month === idx + 1);
                return { name, total: monthData?.total || 0 };
              })}>
                <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                <XAxis dataKey="name" tick={{ fill: themeColors.chartText }} />
                <YAxis tick={{ fill: themeColors.chartText }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)} 
                  contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }}
                  labelStyle={{ color: themeColors.textPrimary }}
                />
                <Bar dataKey="total" fill="#FF6B6B" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Summary Table */}
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', gridColumn: 'span 2', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>Category Summary</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: themeColors.tableHeaderBg }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: themeColors.textPrimary }}>Category</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>Total Spent</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>% of Total</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>Transactions</th>
                </tr>
              </thead>
              <tbody>
                {stats.byCategory?.map((cat, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${themeColors.borderColor}` }}>
                    <td style={{ padding: '12px', color: themeColors.textPrimary }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLORS[idx % COLORS.length], marginRight: '10px', borderRadius: '2px' }}></span>
                      {cat.categoryName}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: themeColors.textPrimary }}>{formatCurrency(cat.total)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: themeColors.textSecondary }}>{((cat.total / stats.yearTotal) * 100).toFixed(1)}%</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: themeColors.textSecondary }}>{cat.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Profit/Loss Tab */}
      {activeTab === 'profit-loss' && (
        <div style={{ display: 'grid', gap: '20px' }}>
          {/* P&L Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            <div style={{ padding: '30px', backgroundColor: themeColors.profitCardBg, borderRadius: '10px', textAlign: 'center', border: `1px solid ${themeColors.borderColor}` }}>
              <div style={{ fontSize: '16px', color: themeColors.profitCardText, marginBottom: '10px' }}>Total Revenue</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: themeColors.profitCardValue }}>{formatCurrency(profitLoss.totalRevenue)}</div>
            </div>
            <div style={{ padding: '30px', backgroundColor: themeColors.expenseCardBg, borderRadius: '10px', textAlign: 'center', border: `1px solid ${themeColors.borderColor}` }}>
              <div style={{ fontSize: '16px', color: themeColors.expenseCardText, marginBottom: '10px' }}>Total Expenses</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: themeColors.expenseCardValue }}>{formatCurrency(profitLoss.totalExpenses)}</div>
            </div>
            <div style={{ padding: '30px', backgroundColor: profitLoss.netProfit >= 0 ? themeColors.netProfitBg : themeColors.netLossBg, borderRadius: '10px', textAlign: 'center', border: `1px solid ${themeColors.borderColor}` }}>
              <div style={{ fontSize: '16px', color: profitLoss.netProfit >= 0 ? themeColors.netProfitText : themeColors.netLossText, marginBottom: '10px' }}>
                Net {profitLoss.netProfit >= 0 ? 'Profit' : 'Loss'}
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: profitLoss.netProfit >= 0 ? themeColors.netProfitValue : themeColors.netLossValue }}>
                {formatCurrency(Math.abs(profitLoss.netProfit))}
              </div>
            </div>
          </div>

          {/* Monthly P&L Chart */}
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>Monthly Profit/Loss Trend ({selectedYear})</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={profitLoss.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                <XAxis dataKey="month" tick={{ fill: themeColors.chartText }} />
                <YAxis tick={{ fill: themeColors.chartText }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)} 
                  contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }}
                  labelStyle={{ color: themeColors.textPrimary }}
                />
                <Legend wrapperStyle={{ color: themeColors.chartText }} />
                <Bar dataKey="revenue" name="Revenue" fill="#4ECDC4" />
                <Bar dataKey="expenses" name="Expenses" fill="#FF6B6B" />
                <Line type="monotone" dataKey="profit" name="Profit/Loss" stroke="#2196F3" strokeWidth={3} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Details Table */}
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>Monthly Breakdown</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: themeColors.tableHeaderBg }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: themeColors.textPrimary }}>Month</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>Revenue</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>Expenses</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>Profit/Loss</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary }}>Margin</th>
                </tr>
              </thead>
              <tbody>
                {profitLoss.monthly?.map((month, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${themeColors.borderColor}` }}>
                    <td style={{ padding: '12px', fontWeight: 'bold', color: themeColors.textPrimary }}>{month.month}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#4ECDC4' }}>{formatCurrency(month.revenue)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#FF6B6B' }}>{formatCurrency(month.expenses)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: month.profit >= 0 ? '#4ECDC4' : '#FF6B6B' }}>
                      {formatCurrency(month.profit)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: themeColors.textSecondary }}>
                      {month.revenue > 0 ? `${((month.profit / month.revenue) * 100).toFixed(1)}%` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Expense Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '30px', width: '500px', maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Category *</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, boxSizing: 'border-box', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Date *</label>
                <input
                  type="date"
                  value={formData.expenseDate}
                  onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, boxSizing: 'border-box', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  required
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, boxSizing: 'border-box', minHeight: '80px', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  placeholder="Enter description"
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Vendor Name</label>
                <input
                  type="text"
                  value={formData.vendorName}
                  onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, boxSizing: 'border-box', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  placeholder="Enter vendor name"
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Receipt No</label>
                <input
                  type="text"
                  value={formData.receiptNo}
                  onChange={(e) => setFormData({ ...formData, receiptNo: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, boxSizing: 'border-box', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  placeholder="Enter receipt number"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  style={{ padding: '10px 20px', backgroundColor: themeColors.isDark ? '#475569' : '#ccc', color: themeColors.textPrimary, border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', backgroundColor: '#FF6B6B', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  {editingExpense ? 'Update' : 'Add'} Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: themeColors.cardBg, borderRadius: '10px', padding: '30px', width: '400px', border: `1px solid ${themeColors.borderColor}` }}>
            <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>Add Expense Category</h3>
            <form onSubmit={handleAddCategory}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Category Name *</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, boxSizing: 'border-box', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  placeholder="Enter category name"
                  required
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: themeColors.textPrimary }}>Icon (emoji)</label>
                <input
                  type="text"
                  value={newCategory.icon}
                  onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                  style={{ width: '100px', padding: '10px', borderRadius: '5px', border: `1px solid ${themeColors.inputBorder}`, fontSize: '20px', textAlign: 'center', backgroundColor: themeColors.inputBg, color: themeColors.inputText }}
                  placeholder="💰"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowCategoryModal(false); setNewCategory({ name: '', icon: '💰' }); }}
                  style={{ padding: '10px 20px', backgroundColor: themeColors.isDark ? '#475569' : '#ccc', color: themeColors.textPrimary, border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', backgroundColor: '#4ECDC4', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Add Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
